import z from "zod"
import type { FindProductionSitesForSupplierResponseDto } from "~/gen/api/types/FindProductionSitesForSupplierResponseDto"

export type ParentCompanyLink = {
  readonly id?: string
  readonly name: string
}

export type BusinessRelationshipOption = {
  readonly label: string
  readonly value: string
}

export function toParentCompanyLink(value: unknown): ParentCompanyLink {
  if (typeof value !== "object" || value === null) {
    return {
      name: "",
    }
  }

  const idRaw = Reflect.get(value, "id")
  const nameRaw = Reflect.get(value, "name")

  return {
    ...(typeof idRaw === "string" ? { id: idRaw } : {}),
    name: typeof nameRaw === "string" ? nameRaw : "",
  }
}

export function getCanEditParentCompany(
  productionSite: FindProductionSitesForSupplierResponseDto | undefined
): boolean {
  if (productionSite === undefined) {
    return false
  }

  const canEditParentCompanyRaw = Reflect.get(
    productionSite,
    "canEditParentCompany"
  )
  return canEditParentCompanyRaw === true
}

export function getParentCompanyForEdit(
  productionSite: FindProductionSitesForSupplierResponseDto | undefined
): ParentCompanyLink | undefined {
  if (productionSite === undefined) {
    return undefined
  }

  const parentCompanyRaw = Reflect.get(productionSite, "parentCompany")
  if (typeof parentCompanyRaw !== "object" || parentCompanyRaw === null) {
    return undefined
  }

  const idRaw = Reflect.get(parentCompanyRaw, "id")
  const nameRaw = Reflect.get(parentCompanyRaw, "name")

  if (typeof nameRaw !== "string") {
    return undefined
  }

  return {
    ...(typeof idRaw === "string" ? { id: idRaw } : {}),
    name: nameRaw,
  }
}

export function resolveInitialParentCompanyValue(
  canEditParentCompany: boolean,
  parentCompany: ParentCompanyLink,
  options: ReadonlyArray<BusinessRelationshipOption>
): string {
  if (!canEditParentCompany) {
    return parentCompany.name
  }

  return resolveEditableParentCompanyInitialValue(parentCompany, options)
}

export function resolveParentCompanyOrganizationId(
  parentCompany: ParentCompanyLink,
  options: ReadonlyArray<BusinessRelationshipOption>
): string {
  const trimmedId = parentCompany.id?.trim() ?? ""
  if (
    trimmedId.length > 0 &&
    options.some((option) => option.value === trimmedId)
  ) {
    return trimmedId
  }

  const matchedOption = options.find(
    (option) => option.label === parentCompany.name
  )
  return matchedOption?.value ?? ""
}

export function isParentCompanyLinkResolved(
  parentCompany: ParentCompanyLink,
  options: ReadonlyArray<BusinessRelationshipOption>
): boolean {
  return resolveParentCompanyOrganizationId(parentCompany, options).length > 0
}

export function hasParentCompanySelectionChanged(
  selectedParentCompanyId: string,
  originalParentCompanyId: string,
  options: ReadonlyArray<BusinessRelationshipOption>
): boolean {
  const trimmedSelection = selectedParentCompanyId.trim()
  if (trimmedSelection.length === 0) {
    return false
  }

  if (trimmedSelection === originalParentCompanyId) {
    return false
  }

  if (options.some((option) => option.value === trimmedSelection)) {
    return true
  }
  const organizationIdParse = z.uuid().safeParse(trimmedSelection)
  return organizationIdParse.success
}

export function shouldShowParentCompanyReadOnlyFallback(
  canEditParentCompany: boolean,
  parentCompany: ParentCompanyLink,
  options: ReadonlyArray<BusinessRelationshipOption>
): boolean {
  if (!canEditParentCompany) {
    return false
  }

  if (parentCompany.name.trim().length === 0) {
    return false
  }

  return !isParentCompanyLinkResolved(parentCompany, options)
}

export function resolveEditableParentCompanyInitialValue(
  parentCompany: ParentCompanyLink,
  options: ReadonlyArray<BusinessRelationshipOption>
): string {
  const resolvedId = resolveParentCompanyOrganizationId(parentCompany, options)
  if (resolvedId.length > 0) {
    return resolvedId
  }
  return ""
}
